import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getDashboard, getAtAGlance } from "../lib/architectPortal";
import ArchitectPortalSubnav from "../components/ArchitectPortalSubnav";
import StatusPill from "../components/StatusPill";
import {
  ChevronRight,
  Inbox,
  Hammer,
  Plus,
  ExternalLink,
  ShieldAlert,
  Clock,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import { WaitingOnPill } from "../lib/ownerUi";

/* Stages the architect is personally responsible for moving forward. */
const ARCH_BLOCKED = new Set(["Consultation", "Build"]);
const CITY_BLOCKED = new Set(["Expedited Review", "Construction Inspection"]);

export default function ArchitectDashboard() {
  const { user } = useRole();
  const [data, setData] = useState(null);
  const [glance, setGlance] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getDashboard(user.id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e));
    getAtAGlance(user.id)
      .then((g) => alive && setGlance(g))
      .catch(() => alive && setGlance({ items: [] }));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (err) {
    return (
      <section data-testid="architect-dashboard-error">
        <ArchitectPortalSubnav />
        <p style={{ color: "#A23B2E" }}>
          Could not load your dashboard. Please try again shortly.
        </p>
      </section>
    );
  }

  if (!data) {
    return (
      <section data-testid="architect-dashboard-loading">
        <ArchitectPortalSubnav />
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading your dashboard…
        </div>
      </section>
    );
  }

  const arch = data.architect || {};
  const stats = data.stats || {};
  const firstName = (arch.name || user?.name || "")
    .split(",")[0]
    .split(" ")[0];
  const pendingVerif = arch.pending_verification || {};
  const hasPendingVerif = pendingVerif && Object.keys(pendingVerif).length > 0;
  const summary = [
    arch.firm,
    `${stats.open_inquiries || 0} open inquir${stats.open_inquiries === 1 ? "y" : "ies"}`,
    `${stats.active_consultations || 0} active consultation${stats.active_consultations === 1 ? "" : "s"}`,
  ].filter(Boolean).join(" · ");

  return (
    <section
      className="animate-fade-in-up"
      data-testid="architect-dashboard"
      style={{ paddingBottom: 96 }}
    >
      <ArchitectPortalSubnav />

      {/* ── Editorial header — generous breathing room. ─────────────── */}
      <header
        className="flex items-end justify-between flex-wrap"
        style={{ gap: 24, paddingTop: 8, paddingBottom: 28 }}
      >
        <div style={{ minWidth: 0, maxWidth: 820 }}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 32,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Architect Portal
            </span>
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(28px, 3.6vw, 40px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
              margin: "16px 0 0",
            }}
            data-testid="architect-dashboard-h1"
          >
            {timeOfDayGreeting()}, {firstName || "there"}.
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 15,
              lineHeight: 1.55,
              marginTop: 12,
              maxWidth: 640,
            }}
            data-testid="architect-dashboard-summary"
          >
            {summary}
          </div>
        </div>
        <div className="flex items-center flex-shrink-0" style={{ gap: 10 }}>
          <Link
            to="/architect/designs/new"
            className="wn-btn wn-btn-primary"
            data-testid="dashboard-submit-design"
          >
            <Plus size={14} /> Submit new design
          </Link>
          {arch.id && (
            <Link
              to={`/architects/${arch.id}`}
              className="wn-btn wn-btn-tertiary"
              data-testid="dashboard-view-profile"
            >
              View public profile <ExternalLink size={12} />
            </Link>
          )}
        </div>
      </header>

      {/* Pending verification banner (P2.i) — kept compact, top of page. */}
      {hasPendingVerif && (
        <PendingVerifBanner pending={pendingVerif} />
      )}

      {/* Contracts awaiting countersignature — gated CTA, very visible. */}
      <ContractsToCountersignBanner projects={data.projects || []} />

      {/* ── KPIs ─ wide editorial spread, vertical rules, lots of air ── */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          borderTop: "1px solid #D9D8E4",
          borderBottom: "1px solid #D9D8E4",
        }}
        data-testid="dashboard-stats"
      >
        <Stat
          value={stats.open_inquiries}
          label="Open inquiries"
          to="/architect/inquiries"
          detail={
            countStale(data.inquiries) > 0
              ? `${countStale(data.inquiries)} stale`
              : null
          }
        />
        <Stat
          value={stats.active_consultations}
          label="Active consultations"
          divider
          detail={null}
        />
        <Stat
          value={stats.active_projects}
          label="Active projects"
          divider
          to="/architect/projects"
          detail={projectsBlockerSummary(data.projects)}
        />
        <Stat
          value={stats.design_total}
          label="Designs published"
          divider
          to="/architect/designs"
          detail={designsDetailLine(stats.design_counts || {})}
        />
      </div>

      {/* ── Catalog strip — spacious, full-width, with breathing room ── */}
      <DesignsStrip counts={stats.design_counts || {}} total={stats.design_total || 0} />

      {/* ── Active work — 2 generous columns, Inquiries & Projects ── */}
      <SectionHeader
        eyebrow="Active work"
        title="Inquiries & projects on your desk."
      />
      <div
        className="grid"
        style={{
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 40,
          marginTop: 20,
        }}
        data-testid="dashboard-columns"
      >
        <Column
          icon={<Inbox size={16} />}
          title="Inquiries waiting on you."
          seeAllTo="/architect/inquiries"
          empty={
            <EmptyBlock
              title="No inquiries yet."
              body="Your public profile is live — work usually arrives through there."
              linkTo={`/architects/${arch.id || ""}`}
              linkLabel="See your public profile"
            />
          }
          items={data.inquiries.map((c) => (
            <InquiryRow key={c.id} consult={c} />
          ))}
        />
        <Column
          icon={<Hammer size={16} />}
          title="Projects in progress."
          seeAllTo="/architect/projects"
          empty={
            <EmptyBlock
              title="No active projects yet."
              body="Projects begin when you activate a consultation."
            />
          }
          items={data.projects.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        />
      </div>

      {/* ── Recent activity + At-a-glance side card ── */}
      <SectionHeader
        eyebrow="Trail"
        title="Recent activity."
        style={{ marginTop: 56 }}
      />
      <div
        className="grid mt-5"
        style={{
          gridTemplateColumns: "minmax(0, 720px) minmax(0, 1fr)",
          gap: 40,
        }}
        data-testid="trail-row"
      >
        <div
          className="wn-card"
          style={{ padding: "20px 24px" }}
          data-testid="dashboard-activity"
        >
          {renderActivity(data.activity).length === 0 ? (
            <EmptyBlock
              title="Quiet so far."
              body="Your activity will appear here as you work."
            />
          ) : (
            renderActivity(data.activity)
          )}
        </div>
        <AtAGlanceCard glance={glance} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function ContractsToCountersignBanner({ projects }) {
  const pending = (projects || []).filter(
    (p) => p.purchase_contract?.status === "owner_signed",
  );
  if (!pending.length) return null;
  return (
    <div
      data-testid="dashboard-contracts-countersign"
      style={{
        marginTop: 20,
        marginBottom: 4,
        border: "1px solid #B14E2F",
        borderRadius: 6,
        background: "rgba(177,78,47,0.04)",
        padding: "18px 22px",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Action required · Contracts awaiting your countersignature
        </span>
      </div>
      <h2
        className="mt-2"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 22,
          color: "#1B1F4B",
          margin: 0,
          lineHeight: 1.2,
        }}
      >
        {pending.length} client
        {pending.length === 1 ? "" : "s"} signed and paid — waiting on you.
      </h2>
      <ul
        className="mt-4"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
        data-testid="dashboard-contracts-countersign-list"
      >
        {pending.map((p) => {
          const c = p.purchase_contract || {};
          const op = c.owner_party || {};
          const fees = c.fee_breakdown || {};
          const amount = fees.total || p.plan_purchase_receipt?.amount || 0;
          const signedAt = c.owner_signature?.signed_at
            ? new Date(c.owner_signature.signed_at).toLocaleDateString("en-US", {
                month: "short", day: "numeric", year: "numeric",
              })
            : "—";
          return (
            <li
              key={p.id}
              data-testid={`dashboard-contracts-countersign-row-${p.id}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 16,
                alignItems: "center",
                padding: "12px 0",
                borderTop: "1px solid rgba(177,78,47,0.18)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 500,
                    color: "#1B1F4B",
                    fontSize: 16,
                    lineHeight: 1.25,
                  }}
                >
                  {(c.design?.name) || p.design?.name || "Project"}
                </div>
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 13,
                    marginTop: 2,
                  }}
                >
                  {op.full_name || op.organization || "Owner"} signed{" "}
                  {signedAt}
                </div>
              </div>
              <div
                className="tabular-nums"
                style={{
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 800,
                  color: "#1B1F4B",
                  fontSize: 18,
                }}
              >
                ${Number(amount).toLocaleString()}
              </div>
              <Link
                to={`/architect/projects/${p.id}`}
                className="wn-btn wn-btn-primary"
                data-testid={`dashboard-contracts-countersign-cta-${p.id}`}
              >
                Review →
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AtAGlanceCard({ glance }) {
  // Render a skeleton until the API has returned. Same height as a populated
  // card so the grid doesn't jump.
  if (!glance) {
    return (
      <div
        className="wn-card"
        style={{ padding: "20px 22px", alignSelf: "start" }}
        data-testid="at-a-glance-loading"
      >
        <div className="wn-label" style={{ color: "#6C6B85" }}>
          At a glance · this month
        </div>
        <div
          style={{ marginTop: 14, color: "#9AA0C0", fontStyle: "italic", fontSize: 13 }}
        >
          Loading…
        </div>
      </div>
    );
  }
  const items = glance.items || [];
  return (
    <div
      className="wn-card"
      style={{ padding: "20px 22px", alignSelf: "start" }}
      data-testid="at-a-glance"
    >
      <div className="flex items-center" style={{ gap: 8 }}>
        <BarChart3 size={14} color="#B14E2F" />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          At a glance · this month
        </span>
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 12.5,
          marginTop: 6,
        }}
      >
        Last 30 days of marketplace activity on your catalog.
      </div>
      <div className="mt-4 space-y-3">
        {items.map((it) => (
          <GlanceRow key={it.key} item={it} />
        ))}
      </div>
    </div>
  );
}

function GlanceRow({ item }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{
        gap: 12,
        paddingBottom: 10,
        borderBottom: "1px solid #ECEBF1",
      }}
      data-testid={`glance-${item.key}`}
    >
      <div className="min-w-0">
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#272636",
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          {item.label}
        </div>
        <div
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 12,
            marginTop: 2,
          }}
        >
          {item.sublabel}
        </div>
      </div>
      <div
        className="tabular-nums"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 26,
          color: item.value > 0 ? "#1B1F4B" : "#9AA0C0",
          lineHeight: 1,
          letterSpacing: "-0.01em",
        }}
      >
        {item.value}
      </div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, style }) {
  return (
    <div style={{ marginTop: 48, ...(style || {}) }} data-testid={`section-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 22,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          {eyebrow}
        </span>
      </div>
      <h2
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          color: "#1B1F4B",
          lineHeight: 1.2,
          margin: "8px 0 0",
        }}
      >
        {title}
      </h2>
    </div>
  );
}

function Stat({ value, label, divider, to, detail }) {
  const inner = (
    <div
      style={{
        padding: "22px 28px 22px",
        borderLeft: divider ? "1px solid #D9D8E4" : "none",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textDecoration: "none",
      }}
    >
      <div
        className="tabular-nums"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 36,
          color: "#1B1F4B",
          lineHeight: 1,
          letterSpacing: "-0.01em",
        }}
        data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {value == null ? "—" : value}
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          fontWeight: 700,
          color: "#6C6B85",
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {label}
      </div>
      {detail && (
        <div
          style={{
            marginTop: 4,
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#9AA0C0",
            fontSize: 12.5,
            lineHeight: 1.4,
          }}
        >
          {detail}
        </div>
      )}
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>
      {inner}
    </Link>
  ) : (
    inner
  );
}

function PendingVerifBanner({ pending }) {
  const fields = Object.keys(pending).filter((k) => k !== "requested_at");
  const requestedAt = pending.requested_at;
  return (
    <div
      className="flex items-start"
      style={{
        gap: 12,
        padding: "12px 16px",
        marginTop: 0,
        marginBottom: 24,
        border: "1px solid #E4D5B5",
        borderLeft: "3px solid #B98A3C",
        background: "#FAF6EC",
        borderRadius: 4,
      }}
      data-testid="pending-verif-banner"
    >
      <ShieldAlert size={18} color="#8a651f" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#1B1F4B", fontSize: 14 }}>
          Verification request under admin review
        </div>
        <div
          style={{
            color: "#6C6B85",
            fontSize: 13,
            marginTop: 2,
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
          }}
        >
          {fields.length > 0 && (
            <>Updates parked: {fields.map(humanFieldLabel).join(", ")}. </>
          )}
          {requestedAt && <>Submitted {relativeDate(requestedAt)}.</>}
        </div>
      </div>
    </div>
  );
}

function DesignsStrip({ counts, total }) {
  // Hide entirely if architect has no designs at all yet.
  if (!total) return null;
  const ORDER = ["Approved", "In Review", "Needs Revision", "Draft", "Rejected"];
  return (
    <div
      style={{
        marginTop: 28,
        padding: "22px 28px",
        background: "#F4EFE6",
        borderRadius: 4,
        border: "1px solid #E6DFD0",
      }}
      data-testid="dashboard-designs-strip"
    >
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 20 }}>
        <div style={{ minWidth: 0, maxWidth: 320 }}>
          <span className="wn-label" style={{ color: "#8a651f" }}>
            Marketplace catalog
          </span>
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 20,
              color: "#1B1F4B",
              margin: "8px 0 0",
              lineHeight: 1.25,
            }}
          >
            {total} design{total === 1 ? "" : "s"} on file.
          </h3>
          <Link
            to="/architect/designs"
            className="wn-btn wn-btn-tertiary"
            style={{ paddingLeft: 0, marginTop: 6 }}
          >
            Manage designs <ChevronRight size={14} />
          </Link>
        </div>
        <div
          className="flex flex-wrap items-center"
          style={{ gap: 22, flex: 1, justifyContent: "flex-end" }}
        >
          {ORDER.map((k) => (
            <CatalogStat key={k} status={k} count={counts[k] || 0} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CatalogStat({ status, count }) {
  const isZero = count === 0;
  return (
    <div
      style={{
        minWidth: 78,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        opacity: isZero ? 0.4 : 1,
      }}
      data-testid={`designs-strip-${status.replace(/\s+/g, "_")}`}
    >
      <div
        className="tabular-nums"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 26,
          color: "#1B1F4B",
          lineHeight: 1,
          letterSpacing: "-0.01em",
        }}
      >
        {count}
      </div>
      <StatusPill value={status} variant="soft" />
    </div>
  );
}

function Column({ icon, title, seeAllTo, items, empty }) {
  return (
    <div>
      <div className="flex items-center gap-2" style={{ marginBottom: 14 }}>
        <span style={{ color: "#B14E2F" }}>{icon}</span>
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 17,
            color: "#1B1F4B",
            margin: 0,
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </h3>
      </div>
      <div className="space-y-2.5">
        {items && items.length > 0 ? items : empty}
      </div>
      {seeAllTo && items && items.length > 0 && (
        <Link
          to={seeAllTo}
          className="wn-btn wn-btn-tertiary"
          style={{ paddingLeft: 0, marginTop: 12 }}
        >
          See all <ChevronRight size={14} />
        </Link>
      )}
    </div>
  );
}

function InquiryRow({ consult }) {
  const stale = inquiryStaleLabel(consult);
  const LABEL_OVERRIDE = { "Consultation Requested": "NEW" };
  return (
    <Link
      to={`/architect/inquiries/${consult.id}`}
      className="wn-card block"
      style={{ padding: "16px 18px", textDecoration: "none" }}
      data-testid={`dashboard-inquiry-${consult.id}`}
    >
      <div className="flex items-start justify-between" style={{ gap: 14 }}>
        <div className="min-w-0">
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 15,
              color: "#1B1F4B",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {consult.parcel?.address || "—"}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.5,
              marginTop: 4,
            }}
          >
            {consult.owner?.name || consult.owner?.organization || "—"}
            {" · "}
            Requested {relativeDate(consult.opened_at)}
          </div>
          {stale && (
            <div
              className="inline-flex items-center"
              style={{
                gap: 5,
                marginTop: 8,
                padding: "3px 9px",
                background: "#FAF6EC",
                border: "1px solid #E4D5B5",
                borderRadius: 999,
                color: "#8a651f",
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
              data-testid={`inquiry-stale-${consult.id}`}
            >
              <AlertCircle size={10} strokeWidth={2.4} />
              {stale}
            </div>
          )}
        </div>
        <StatusPill
          value={consult.status}
          label={LABEL_OVERRIDE[consult.status]}
        />
      </div>
    </Link>
  );
}

function ProjectRow({ project }) {
  const sla = expReviewSLA(project);
  return (
    <div
      className="wn-card"
      style={{ padding: "16px 18px" }}
      data-testid={`dashboard-project-${project.id}`}
    >
      <div className="flex items-start justify-between" style={{ gap: 14 }}>
        <div className="min-w-0">
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 15,
              color: "#1B1F4B",
              lineHeight: 1.3,
            }}
          >
            {project.design?.name || project.id}
          </div>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.5,
              marginTop: 4,
            }}
          >
            {project.parcel?.address || "—"}
          </div>
          <div
            className="flex items-center flex-wrap"
            style={{ gap: 10, marginTop: 10 }}
          >
            <WaitingOnPill
              stage={project.current_stage}
              size="sm"
              testId={`project-blocker-${project.id}`}
            />
            {sla && (
              <span
                className="inline-flex items-center"
                style={{
                  gap: 5,
                  color: "#3A5563",
                  fontSize: 12.5,
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                }}
                data-testid={`project-sla-${project.id}`}
              >
                <Clock size={11} strokeWidth={1.8} />
                City decision by {sla}
              </span>
            )}
          </div>
        </div>
        <StatusPill value={project.current_stage} variant="soft" />
      </div>
    </div>
  );
}

function renderActivity(activity) {
  // P3.j — group by relative day.
  const groups = groupByDay(activity || []);
  if (groups.length === 0) return [];
  return groups.map(([label, entries]) => (
    <ActivityGroup key={label} label={label} entries={entries} />
  ));
}

function ActivityGroup({ label, entries }) {
  return (
    <div
      data-testid={`activity-group-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className="wn-label"
        style={{
          color: "#6C6B85",
          paddingBottom: 10,
          borderBottom: "1px solid #ECEBF1",
        }}
      >
        {label} · {entries.length} event{entries.length === 1 ? "" : "s"}
      </div>
      <div
        style={{
          position: "relative",
          paddingLeft: 18,
          paddingTop: 4,
        }}
      >
        {/* Vertical timeline rail */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 4,
            top: 16,
            bottom: 12,
            width: 1,
            background: "#ECEBF1",
          }}
        />
        {entries.map((e, i) => (
          <ActivityRow key={e.id || i} entry={e} />
        ))}
      </div>
    </div>
  );
}

function ActivityRow({ entry }) {
  const isProfileUpdate = entry.action === "profile_updated";
  return (
    <div
      className="flex items-start"
      style={{
        gap: 14,
        padding: "10px 0",
        position: "relative",
      }}
      data-testid="activity-row"
    >
      {/* Dot — positioned over the rail */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: -18,
          top: 16,
          width: 9,
          height: 9,
          borderRadius: 999,
          backgroundColor: "#B14E2F",
          border: "2px solid #FBFAFD",
          boxShadow: "0 0 0 1px #B14E2F",
          flexShrink: 0,
        }}
      />
      <div className="min-w-0" style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 13.5,
            color: "#272636",
            lineHeight: 1.5,
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 500,
          }}
        >
          {humanAction(entry)}
        </div>
        {isProfileUpdate && entry.note && (
          <FieldChips csv={entry.note} testId={entry.id} />
        )}
      </div>
      <span
        className="tabular-nums flex-shrink-0"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#9AA0C0",
          fontSize: 12,
          marginTop: 3,
          whiteSpace: "nowrap",
        }}
      >
        {entry.at ? new Date(entry.at).toLocaleTimeString("en-US", {
          hour: "numeric", minute: "2-digit",
        }) : "—"}
      </span>
    </div>
  );
}

function FieldChips({ csv, testId }) {
  const fields = (csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (fields.length === 0) return null;
  return (
    <details
      style={{ marginTop: 4 }}
      data-testid={`activity-fields-${testId || "x"}`}
    >
      <summary
        style={{
          cursor: "pointer",
          color: "#6C6B85",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          listStyle: "none",
        }}
      >
        Show fields
      </summary>
      <div
        className="mt-1.5 flex flex-wrap"
        style={{ gap: 4 }}
      >
        {fields.map((f) => (
          <span
            key={f}
            style={{
              padding: "1px 7px",
              borderRadius: 999,
              border: "1px solid #D9D8E4",
              color: "#272636",
              fontSize: 10.5,
              fontWeight: 500,
              fontFamily: '"Nunito Sans", sans-serif',
            }}
          >
            {humanFieldLabel(f)}
          </span>
        ))}
      </div>
    </details>
  );
}

function EmptyBlock({ title, body, linkTo, linkLabel }) {
  return (
    <div
      className="wn-card"
      style={{ padding: "16px 18px" }}
      data-testid="dashboard-empty"
    >
      <div
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 15,
          color: "#1B1F4B",
        }}
      >
        {title}
      </div>
      <p
        style={{
          marginTop: 4,
          color: "#6C6B85",
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        {body}
      </p>
      {linkTo && (
        <Link
          to={linkTo}
          className="wn-btn wn-btn-tertiary mt-2"
          style={{ paddingLeft: 0 }}
        >
          {linkLabel} <ChevronRight size={14} />
        </Link>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/* Number of inquiries older than 30 days (used as KPI detail subline). */
function countStale(inquiries) {
  return (inquiries || []).filter((c) => inquiryStaleLabel(c)).length;
}

/* Summarize project blockers for the KPI subline. */
function projectsBlockerSummary(projects) {
  const counts = { you: 0, city: 0, owner: 0 };
  (projects || []).forEach((p) => {
    const w = archProjectBlocker(p.current_stage).who;
    if (counts[w] !== undefined) counts[w] += 1;
  });
  const parts = [];
  if (counts.you) parts.push(`${counts.you} on you`);
  if (counts.city) parts.push(`${counts.city} on city`);
  if (counts.owner) parts.push(`${counts.owner} on owner`);
  return parts.join(" · ") || null;
}

/* Designs KPI subline. Highlights anything needing the architect's
   attention (Needs Revision, In Review). */
function designsDetailLine(counts) {
  const nr = counts["Needs Revision"] || 0;
  const ir = counts["In Review"] || 0;
  const parts = [];
  if (nr) parts.push(`${nr} need revision`);
  if (ir) parts.push(`${ir} in review`);
  return parts.length ? parts.join(" · ") : null;
}

function archProjectBlocker(stage) {
  if (ARCH_BLOCKED.has(stage)) return { who: "you", color: "#8F3C23" };
  if (CITY_BLOCKED.has(stage)) return { who: "city", color: "#3A5563" };
  if (stage === "Selection" || stage === "Purchase")
    return { who: "owner", color: "#1B1F4B" };
  return { who: "—", color: "#6C6B85" };
}

/* Returns ISO month/day for the 14-day SLA on Expedited Review entry. */
function expReviewSLA(project) {
  if (project.current_stage !== "Expedited Review") return null;
  const entry = (project.stage_history || []).find(
    (h) => h.stage === "Expedited Review",
  );
  if (!entry?.at) return null;
  const due = new Date(new Date(entry.at).getTime() + 14 * 86400000);
  return due.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function inquiryStaleLabel(consult) {
  if (consult.status !== "Consultation Requested") return null;
  if (!consult.opened_at) return null;
  const days = Math.floor(
    (Date.now() - new Date(consult.opened_at).getTime()) / 86400000,
  );
  if (days < 30) return null;
  return days < 60 ? `Stale · ${days} days` : `Stale · ${Math.floor(days / 7)} weeks`;
}

/* Full action-verb humanizer (P0.a). Any action coming from the backend
   activity_log must have a friendly user-facing sentence here. New verbs
   fall through to a Title-Case humanization so we never leak `snake_case`
   slugs to the UI again. */
function humanAction(entry) {
  const a = entry.action || "";
  const note = (entry.note || "").trim();
  switch (a) {
    case "message_sent":
      return "You sent a message on a consultation.";
    case "consultation_requested":
      return "An owner requested a consultation.";
    case "consultation_activated":
      return "You activated a consultation — a new project was created.";
    case "consultation_declined":
      return "You declined a consultation.";
    case "design_submitted":
      return "You submitted a design for review.";
    case "resubmitted":
      return "You resubmitted a design after revisions.";
    case "iteration_requested":
      return note
        ? `An owner requested an iteration: "${truncate(note, 90)}".`
        : "An owner requested an iteration on a consultation.";
    case "plans_purchased":
      return "An owner purchased plans — the project advanced to Build.";
    case "document_generated":
      return note
        ? `You generated a document (${humanFieldLabel(note)}).`
        : "You generated a project document.";
    case "milestone_verified":
      return "You verified a construction milestone.";
    case "visit_logged":
      return "You logged a site visit.";
    case "signed_to_dbns":
      return "You signed a project to DBNS.";
    case "saved":
      return "An owner saved one of your designs.";
    case "unsaved":
      return "An owner removed one of your designs from their saved list.";
    case "profile_updated": {
      const fields = note.split(",").map((s) => s.trim()).filter(Boolean);
      const n = fields.length;
      if (n === 0) return "You updated your profile.";
      return `You updated ${n} profile field${n === 1 ? "" : "s"}.`;
    }
    case "architect.verification_requested":
      return "You requested admin re-verification on license details.";
    case "architect.verification_approved":
      return "Admin approved your license update.";
    case "architect.verification_rejected":
      return "Admin declined your license update.";
    case "architect.profile_published":
      return "Your public profile was published.";
    case "architect.profile_unpublished":
      return "Your public profile was unpublished.";
    case "architect.profile_edited_by_admin":
      return "An admin edited your profile on your behalf.";
    case "impersonation.started":
      return "An admin began viewing the platform as you.";
    case "impersonation.stopped":
      return "An admin stopped viewing the platform as you.";
    default:
      // Friendly fallback for any verb we haven't mapped — still readable.
      return (
        a
          .replace(/^architect\./, "")
          .replace(/[._]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()) + "."
      );
  }
}

function humanFieldLabel(field) {
  if (!field) return "";
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function truncate(s, n) {
  if (!s || s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

function relativeDate(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 14) return `${days} days ago`;
    if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/* Group label for the activity grouping (P3.j). Returns "Today",
   "Yesterday", "This week", or an absolute month-day string. */
function dayBucket(iso) {
  if (!iso) return "Earlier";
  try {
    const t = new Date(iso).getTime();
    const days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return "This week";
    if (days < 30) return "This month";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  } catch {
    return "Earlier";
  }
}

function groupByDay(activity) {
  const order = [];
  const map = {};
  for (const a of activity) {
    const k = dayBucket(a.at);
    if (!map[k]) {
      map[k] = [];
      order.push(k);
    }
    map[k].push(a);
  }
  return order.map((k) => [k, map[k]]);
}
